import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Card from "../components/ui/Card";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import { Parse } from "../lib/parse";
import { applyPreferredLanguage } from "../i18n";
import { useBrandingConfig } from "../hooks/useBrandingConfig";
import logoDanasign from "../assets/logo-danasign.png";
import {
  bootstrapLegacyStorage,
  clearSession,
  getLandingForRole,
  getSessionToken,
  persistExtendedUser,
  persistSession
} from "../lib/auth";

function LoginPage({ branding: brandingProp }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { branding: hookBranding } = useBrandingConfig();
  const branding = brandingProp?.appName ? brandingProp : hookBranding;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isInvalidSessionError = (value) =>
    value?.code === 209 || String(value?.message || "").toLowerCase().includes("invalid session token");

  useEffect(() => {
    bootstrapLegacyStorage();
  }, []);

  useEffect(() => {
    const existingToken = getSessionToken();
    if (!existingToken) {
      return;
    }

    let ignore = false;

    const recoverSession = async () => {
      try {
        await Parse.User.become(existingToken);
        const extUser = await Parse.Cloud.run("getUserDetails").catch(() => null);
        if (extUser) {
          persistExtendedUser(extUser);
          const preferredLanguage = extUser?.get ? extUser.get("Language") : extUser?.Language;
          if (preferredLanguage) {
            applyPreferredLanguage(preferredLanguage);
          }
        }
        if (!ignore) {
          navigate("/dashboard/35KBoSgoAK", { replace: true });
        }
      } catch (sessionError) {
        console.error("Session recovery failed", sessionError);
        if (isInvalidSessionError(sessionError)) {
          clearSession();
          bootstrapLegacyStorage();
          try {
            await Parse.User.logOut();
          } catch (logoutError) {
            // Ignore logout failures for invalid/expired sessions.
          }
        }
      }
    };

    recoverSession();

    return () => {
      ignore = true;
    };
  }, [navigate]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    try {
      const user = await Parse.Cloud.run("loginuser", {
        email: email.trim().toLowerCase(),
        password
      });

      if (!user?.sessionToken) {
        throw new Error("Authentication failed");
      }

      await Parse.User.become(user.sessionToken);
      persistSession(user);

      const extUser = await Parse.Cloud.run("getUserDetails");
      const role = extUser?.get ? extUser.get("UserRole") : extUser?.UserRole;
      const landing = getLandingForRole(role);

      persistExtendedUser(extUser);
      const preferredLanguage = extUser?.get ? extUser.get("Language") : extUser?.Language;
      if (preferredLanguage) {
        applyPreferredLanguage(preferredLanguage);
      }
      localStorage.setItem("PageLanding", landing);
      localStorage.setItem("pageType", "dashboard");

      // Always redirect to dashboard after login, ignore previous location
      const targetPath = `/dashboard/${landing}`;

      navigate(targetPath, { replace: true });
    } catch (submitError) {
      console.error("Login error", submitError);
      if (isInvalidSessionError(submitError)) {
        clearSession();
        bootstrapLegacyStorage();
      }
      setError(t("new-ui.auth.invalid-credentials-or-server-error", { defaultValue: "Invalid credentials or server error." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-login-page min-h-screen overflow-hidden bg-app-paper px-4 py-10 md:px-8">
      <div className="shape shape-1" />
      <div className="shape shape-2" />
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2 md:items-center">
        <section className="animate-fade-up">
          <img 
            src={branding?.loginLogo || logoDanasign} 
            alt={branding?.appName || "DANAsign"} 
            className="mb-4 h-14 w-auto md:h-16" 
          />
          <p className="ui-kicker">{branding?.appName || "DANAsign"}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-app-ink md:text-4xl">
            {t("new-ui.auth.hero-title", {
              defaultValue: "Digital signing with secure biometric identity validation."
            })}
          </h1>
          <p className="ui-copy mt-4 max-w-lg text-sm md:text-base">
            {t("new-ui.auth.hero-description", {
              defaultValue:
                "Create signature requests, verify signer identity, and complete every document with traceability in one flow."
            })}
          </p>
        </section>

        <Card className="animate-fade-in md:ml-auto md:max-w-md">
          <h2 className="ui-title text-xl">{t("login", { defaultValue: "Sign in" })}</h2>
          <p className="ui-copy mt-1">{t("new-ui.auth.use-existing-account", { appName: branding?.appName || "DANAsign", defaultValue: "Use your existing {{appName}} account." })}</p>
          <form className="mt-5 space-y-4" onSubmit={onSubmit}>
            <Input
              id="email"
              label={t("email", { defaultValue: "Email" })}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <Input
              id="password"
              label={t("password", { defaultValue: "Password" })}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <div className="flex justify-end">
              <Link
                to="/forgetpassword"
                className="text-sm font-medium text-[var(--app-brand)] hover:text-[var(--app-brand-dark)] hover:underline"
              >
                {t("forgot-password", { defaultValue: "Forgot password?" })}
              </Link>
            </div>
            {error ? <p className="text-sm text-app-danger">{error}</p> : null}
            <Button className="w-full" type="submit" disabled={loading}>
              {loading
                ? t("new-ui.auth.signing-in", { defaultValue: "Signing in..." })
                : t("new-ui.common.continue", { defaultValue: "Continue" })}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default LoginPage;
